import { IEditUserProfileRequest, ISignInRequest, ISignUpRequest, ISubscription, IUser, SubscriptionProduct } from '@cuspar/ywt.ch.model';
import { create } from 'zustand';
import { authAPI } from './api/auth';
import { subscriptionAPI } from './api/subscription';
import { useCartStore } from './cartStore';
import i18n from 'i18next';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  deleteUser,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  confirmPasswordReset,
  verifyPasswordResetCode,
  applyActionCode,
  signInWithPopup,
  GoogleAuthProvider,
  GithubAuthProvider,
  getMultiFactorResolver,
  multiFactor,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import type { AuthProvider, MultiFactorError, MultiFactorResolver, TotpSecret, User, UserCredential } from 'firebase/auth';
import { YWT_PUBLIC_CONFIG } from '@cuspar/ywt.ch.config/public';
import * as Sentry from '@sentry/react';
import { priceAPI } from './api/price';
import { toast } from '@cuspar/ui';
import { ROUTES } from '../routes';
import Stripe from 'stripe';

const app = initializeApp({
  apiKey: YWT_PUBLIC_CONFIG.firebase.apiKey,
  authDomain: YWT_PUBLIC_CONFIG.firebase.authDomain,
});

const auth = getAuth(app);

const googleProvider = new GoogleAuthProvider();
const githubProvider = new GithubAuthProvider();

const mfa = {
  _totpSecret: undefined as undefined | TotpSecret,
  _mfaResolver: undefined as undefined | MultiFactorResolver,
};

/**
 * - `error`: Firebase rejected the sign-in (wrong credentials, popup closed);
 * - `unavailable`: the credentials were fine but our backend could not create
 *   the session. The store has already told the user; do not show "invalid
 *   credentials" for it.
 * - `tooManyAttempts`: Firebase paused sign-ins for this account after repeated failures;
 * - `cancelled`: the visitor closed the Google/GitHub popup. Nothing to report.
 */
export type SignInResult = 'success' | 'mfa' | 'error' | 'unavailable' | 'tooManyAttempts' | 'cancelled';

/** `emailInUse`: an account with this address exists already, so the visitor should sign in instead. */
export type SignUpResult = 'success' | 'emailInUse' | 'error' | 'unavailable';

const HAS_ACCOUNT_KEY = 'ywt.hasAccount';

/**
 * Where to send a visitor who must be signed in: the sign-in form when this
 * browser has signed in before, otherwise the sign-up form. Both link to each other.
 */
export function authEntryRoute(): string {
  try {
    return localStorage.getItem(HAS_ACCOUNT_KEY) ? ROUTES.auth.signin : ROUTES.auth.signup;
  } catch {
    return ROUTES.auth.signup;
  }
}

function rememberHasAccount() {
  try {
    localStorage.setItem(HAS_ACCOUNT_KEY, '1');
  } catch {
    // private mode: the entry links then just default to sign-up
  }
}

const firebaseErrorCode = (error: unknown) => (error as { code?: string } | null)?.code;
const INVALID_ACTION_CODES = ['auth/expired-action-code', 'auth/invalid-action-code', 'auth/user-disabled', 'auth/user-not-found'];

/** Per-user data kept in this store; cleared whenever the signed-in user changes. */
const NO_ACCOUNT_DATA = {
  paymentMethods: [] as Stripe.PaymentMethod[],
  defaultPaymentMethodId: null,
  activeSubscriptions: [] as ISubscription[],
  hasActiveStaticSiteSubscription: false,
  expiredStaticSiteSubscription: null,
};

/**
 * Creates the backend session for a Firebase sign-in. Firebase alone is not a
 * sign-in: without the session cookie every account page bounces back to the
 * sign-in form. If the session cannot be created, sign out of Firebase again
 * and say so.
 */
async function startBackendSession(idToken: string): Promise<boolean> {
  const response = await authAPI.authCallback(idToken, i18n.language);
  if (response.success) {
    rememberHasAccount();
    return true;
  }
  await firebaseSignOut(auth).catch(() => undefined);
  toast.error(i18n.t('auth.signInUnavailable'));
  return false;
}

/** Our own verification email, or Firebase's as a fallback. False only if neither could be sent. */
async function sendVerification(firebaseUser: User, idToken: string): Promise<boolean> {
  const customEmail = await authAPI.sendVerificationEmail(idToken, i18n.language);
  if (customEmail.success && customEmail.data.sent) return true;
  try {
    await sendEmailVerification(firebaseUser);
    return true;
  } catch (error) {
    Sentry.captureException(error, { tags: { source: 'verification_email' } });
    return false;
  }
}

type authStore = {
  isAuthenticating: boolean;
  user?: IUser;
  paymentMethods: Stripe.PaymentMethod[];
  defaultPaymentMethodId: string | null;
  // TOTP/MFA
  isTotpEnrolled: boolean;
  startEnableTotp: () => Promise<{ uri: string; secretKey: string } | undefined>;
  confirmEnableTotp: (verificationCode: string, displayName?: string) => Promise<boolean>;
  disableTotp: () => Promise<boolean>;
  refreshTotpEnrollment: () => void;
  // MFA sign-in flow
  isMfaRequired: boolean;
  completeMfaSignIn: (verificationCode: string) => Promise<boolean>;
  clearMfa: () => void;
  signUp: (body: ISignUpRequest) => Promise<SignUpResult>;
  signInWithEmail: (body: ISignInRequest) => Promise<SignInResult>;
  signWithGoogle: () => Promise<SignInResult>;
  signWithGithub: () => Promise<SignInResult>;
  signInWithProvider: (provider: AuthProvider) => Promise<SignInResult>;
  /** True between a Google/GitHub popup closing and the backend session being ready. */
  isFinishingSignIn: boolean;
  sendPasswordResetEmail: (email: string) => Promise<boolean>;
  /** The email address a reset link is for, or null when the link is invalid or expired. */
  checkPasswordResetLink: (oobCode: string) => Promise<string | null>;
  /** Sets the new password and signs in with it; 'invalid' when the link is invalid or expired. */
  resetPassword: (oobCode: string, body: ISignInRequest) => Promise<SignInResult | 'invalid'>;
  verifyEmail: (oobCode: string) => Promise<'verified' | 'invalid'>;
  /** True for an email/password account whose address is not confirmed yet. */
  needsEmailVerification: boolean;
  refreshEmailVerification: () => Promise<void>;
  resendVerificationEmail: () => Promise<boolean>;
  signOut: () => Promise<void>;
  deleteAccount: (confirmEmail: string) => Promise<boolean>;
  authenticate: () => Promise<void>;
  updateIsAuthenticating: (isAuthenticating: boolean) => void;
  editUserProfile: (body: IEditUserProfileRequest) => Promise<void>;
  handleSignInError: (error: unknown) => SignInResult;
  // Payments
  fetchPaymentMethods: () => Promise<void>;
  makeDefaultPaymentMethod: (paymentMethodId: string) => Promise<boolean>;
  deletePaymentMethod: (paymentMethodId: string) => Promise<boolean>;
  // Subscription
  activeSubscriptions: ISubscription[];
  getActiveSubscriptions: () => Promise<void>;
  hasActiveStaticSiteSubscription: boolean;
  expiredStaticSiteSubscription: ISubscription | null;
  fetchLatestStaticSubscription: () => Promise<void>;
};

export const useAuthStore = create<authStore>((set, get) => ({
  isAuthenticating: true,
  isFinishingSignIn: false,
  user: undefined,
  paymentMethods: [],
  defaultPaymentMethodId: null,
  isTotpEnrolled: false,
  isMfaRequired: false,
  needsEmailVerification: false,
  hasActiveStaticSiteSubscription: false,
  clearMfa: () => {
    mfa._totpSecret = undefined;
    mfa._mfaResolver = undefined;
    set({ isMfaRequired: false });
  },
  completeMfaSignIn: async (verificationCode: string) => {
    try {
      const resolver = mfa._mfaResolver;
      if (!resolver) {
        toast.error(i18n.t('auth.mfaSessionExpired'));
        get().clearMfa();
        return false;
      }
      const totpHint = resolver.hints.find((h) => h.factorId === 'totp');
      if (!totpHint) {
        toast.error(i18n.t('auth.noMfaFactor'));
        return false;
      }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, verificationCode);
      const cred = await resolver.resolveSignIn(assertion);
      const idToken = await cred.user.getIdToken();
      const sessionStarted = await startBackendSession(idToken);
      get().clearMfa();
      if (!sessionStarted) return false;
      get().authenticate();
      return true;
    } catch {
      toast.error(i18n.t('auth.mfaSignInError'));
      return false;
    }
  },
  refreshTotpEnrollment: () => {
    const user = auth.currentUser;
    if (!user) {
      set({ isTotpEnrolled: false });
      return;
    }
    try {
      const enrolled = multiFactor(user).enrolledFactors.some((f) => f.factorId === 'totp');
      set({ isTotpEnrolled: enrolled });
    } catch {
      set({ isTotpEnrolled: false });
    }
  },
  startEnableTotp: async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error(i18n.t('auth.mustBeSignedIn2fa'));
      return;
    }
    try {
      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      mfa._totpSecret = secret;
      const uri = secret.generateQrCodeUrl(user.email!, 'ywt.ch');
      const secretKey = secret.secretKey;
      return { uri, secretKey };
    } catch (e) {
      toast.error(i18n.t('auth.failedStart2fa') + e);
    }
  },
  confirmEnableTotp: async (verificationCode: string, displayName?: string) => {
    const user = auth.currentUser;
    if (!user) {
      toast.error(i18n.t('auth.mustBeSignedIn2fa'));
      return false;
    }
    try {
      const secret = mfa._totpSecret;
      if (!secret) {
        toast.error(i18n.t('auth.totpSecretNotFound'));
        return false;
      }
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, verificationCode);
      await multiFactor(user).enroll(assertion, displayName || 'TOTP');
      mfa._totpSecret = undefined;
      set({ isTotpEnrolled: true });
      toast.success(i18n.t('auth.twoFactorEnabledToast'));
      return true;
    } catch (e) {
      toast.error(i18n.t('auth.failedEnable2fa') + e);
      return false;
    }
  },
  disableTotp: async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error(i18n.t('auth.mustBeSignedInDisable2fa'));
      return false;
    }
    try {
      const factor = multiFactor(user).enrolledFactors.find((f) => f.factorId === 'totp');
      if (!factor) {
        toast.error(i18n.t('auth.noTotpEnabled'));
        return false;
      }
      await multiFactor(user).unenroll(factor.uid);
      set({ isTotpEnrolled: false });
      toast.success(i18n.t('auth.twoFactorDisabled'));
      return true;
    } catch (e) {
      toast.error(i18n.t('auth.failedDisable2fa') + e);
      return false;
    }
  },
  signUp: async (body: ISignUpRequest): Promise<SignUpResult> => {
    let userCredential: UserCredential;
    try {
      userCredential = await createUserWithEmailAndPassword(auth, body.email, body.password);
    } catch (error) {
      if (firebaseErrorCode(error) === 'auth/email-already-in-use') return 'emailInUse';
      Sentry.captureException(error, { tags: { source: 'signup' } });
      toast.error(i18n.t('auth.errorSigningUp'));
      return 'error';
    }
    const idToken = await userCredential.user.getIdToken();
    const provisioned = await authAPI.provisionUser(idToken, i18n.language);
    if (!provisioned.success) {
      await deleteUser(userCredential.user).catch((cleanupError) => {
        Sentry.captureException(cleanupError, { tags: { source: 'signup_cleanup' } });
      });
      toast.error(i18n.t('auth.errorSigningUp'));
      return 'error';
    }
    // The account works without a confirmed address, so a failed email must not fail the sign-up.
    await sendVerification(userCredential.user, idToken);
    // Firebase has signed the new user in; start the session so they carry on where they were.
    if (!(await startBackendSession(idToken))) return 'unavailable';
    set({ needsEmailVerification: !userCredential.user.emailVerified });
    get().authenticate();
    return 'success';
  },
  sendPasswordResetEmail: async (email: string) => {
    const response = await authAPI.sendPasswordResetEmail(email, i18n.language);
    return response.success;
  },
  checkPasswordResetLink: async (oobCode: string) => {
    try {
      return await verifyPasswordResetCode(auth, oobCode);
    } catch {
      return null;
    }
  },
  resetPassword: async (oobCode: string, body: ISignInRequest) => {
    try {
      await confirmPasswordReset(auth, oobCode, body.password);
    } catch (error) {
      if (INVALID_ACTION_CODES.includes(firebaseErrorCode(error) ?? '')) return 'invalid';
      throw error;
    }
    return get().signInWithEmail(body);
  },
  verifyEmail: async (oobCode: string) => {
    try {
      await applyActionCode(auth, oobCode);
    } catch (error) {
      if (INVALID_ACTION_CODES.includes(firebaseErrorCode(error) ?? '')) return 'invalid';
      throw error;
    }
    set({ needsEmailVerification: false });
    await auth.currentUser?.reload().catch(() => undefined);
    return 'verified';
  },
  refreshEmailVerification: async () => {
    await auth.authStateReady();
    const firebaseUser = auth.currentUser;
    // Only email/password accounts are asked to confirm: Google has done it, and the
    // session may belong to an impersonating admin whose own address is not the point.
    if (!firebaseUser || !firebaseUser.providerData.some((p) => p.providerId === 'password') || get().user?.impersonatedByAdminId) {
      set({ needsEmailVerification: false });
      return;
    }
    await firebaseUser.reload().catch(() => undefined);
    set({ needsEmailVerification: !firebaseUser.emailVerified });
  },
  resendVerificationEmail: async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return false;
    return sendVerification(firebaseUser, await firebaseUser.getIdToken());
  },
  signInWithEmail: async (body: ISignInRequest): Promise<SignInResult> => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, body.email, body.password);
      const user = userCredential.user;
      // Get the ID token to pass to your backend
      const idToken = await user.getIdToken();
      if (!(await startBackendSession(idToken))) return 'unavailable';
      get().authenticate();
      return 'success';
    } catch (error) {
      return get().handleSignInError(error);
    }
  },
  signOut: async () => {
    await firebaseSignOut(auth);
    await authAPI.signout();
    set({ user: undefined, ...NO_ACCOUNT_DATA });
    Sentry.setUser(null);
    get().clearMfa();
    // remove the cart from the store
    useCartStore.getState().emptyCart();
  },
  deleteAccount: async (confirmEmail: string) => {
    // The backend wants proof that this browser holds the Firebase credentials, not just
    // a session cookie. Without a Firebase user there is nothing to prove it with.
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      toast.error(i18n.t('account.deleteAccountReauth'));
      return false;
    }
    const idToken = await firebaseUser.getIdToken(true);
    const response = await authAPI.deleteAccount({ confirmEmail, idToken });
    if (!response.success) return false;

    // The Firebase user is already gone server-side; this only clears the local state.
    await firebaseSignOut(auth).catch(() => {});
    set({ user: undefined, ...NO_ACCOUNT_DATA });
    Sentry.setUser(null);
    get().clearMfa();
    useCartStore.getState().emptyCart();
    return true;
  },
  signWithGoogle: () => get().signInWithProvider(googleProvider),
  signWithGithub: () => get().signInWithProvider(githubProvider),
  signInWithProvider: async (provider: AuthProvider) => {
    try {
      const result = await signInWithPopup(auth, provider);
      // The popup has closed, but the backend session takes a moment more: the page shows it.
      set({ isFinishingSignIn: true });
      const idToken = await result.user.getIdToken();
      if (!(await startBackendSession(idToken))) return 'unavailable';
      get().authenticate();
      return 'success';
    } catch (error) {
      return get().handleSignInError(error);
    } finally {
      set({ isFinishingSignIn: false });
    }
  },
  authenticate: async () => {
    set({ isAuthenticating: true });
    try {
      const response = await authAPI.authenticate();
      if (response.success) {
        // A different user than before (another sign-in on this browser, or an
        // admin entering/leaving impersonation): drop the previous user's data.
        const previousUserId = get().user?.id;
        if (previousUserId && previousUserId !== response.data.id) set(NO_ACCOUNT_DATA);
        set({ user: response.data });
        Sentry.setUser({ id: response.data.id });
        if (response.data.locale) i18n.changeLanguage(response.data.locale);
        get().refreshTotpEnrollment();
        postAuthenticate();
      } else {
        Sentry.setUser(null);
        postNonAuthenticate();
      }
    } finally {
      set({ isAuthenticating: false });
    }
  },
  updateIsAuthenticating: (isAuthenticating: boolean) => {
    set({ isAuthenticating });
  },
  editUserProfile: async (body: IEditUserProfileRequest) => {
    const response = await authAPI.editUserProfile(body);
    if (response.success) set({ user: response.data });
  },
  handleSignInError: (error: unknown) => {
    if ((error as MultiFactorError).code === 'auth/multi-factor-auth-required') {
      const resolver = getMultiFactorResolver(auth, error as MultiFactorError);
      mfa._mfaResolver = resolver;
      set({ isMfaRequired: true });
      return 'mfa';
    }
    const code = firebaseErrorCode(error);
    if (code === 'auth/too-many-requests') return 'tooManyAttempts';
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request' || code === 'auth/user-cancelled') return 'cancelled';
    return 'error';
  },
  fetchPaymentMethods: async () => {
    set({ paymentMethods: [], defaultPaymentMethodId: null });
    const response = await authAPI.getPaymentMethods();
    if (response.success) {
      set({
        paymentMethods: response.data.paymentMethods,
        defaultPaymentMethodId: response.data.defaultPaymentMethodId,
      });
    }
  },
  makeDefaultPaymentMethod: async (paymentMethodId: string) => {
    const response = await authAPI.setDefaultPaymentMethod(paymentMethodId);
    if (response.success) {
      set({ defaultPaymentMethodId: paymentMethodId });
      return true;
    }
    return false;
  },
  deletePaymentMethod: async (paymentMethodId: string) => {
    const response = await authAPI.deletePaymentMethod(paymentMethodId);
    if (response.success) {
      set((state) => ({
        paymentMethods: state.paymentMethods.filter((pm) => pm.id !== paymentMethodId),
        defaultPaymentMethodId: state.defaultPaymentMethodId === paymentMethodId ? null : state.defaultPaymentMethodId,
      }));
      return true;
    }
    return false;
  },
  // Subscription
  activeSubscriptions: [],
  expiredStaticSiteSubscription: null,
  getActiveSubscriptions: async () => {
    const response = await authAPI.getActiveSubscriptions();
    if (response.success) {
      const hasActive = response.data.some((s) => s.product === SubscriptionProduct.staticSite);
      set({ activeSubscriptions: response.data, hasActiveStaticSiteSubscription: hasActive });
      // If no active static site subscription, check for expired one
      if (!hasActive) await get().fetchLatestStaticSubscription();
      else set({ expiredStaticSiteSubscription: null });
    }
  },
  fetchLatestStaticSubscription: async () => {
    const response = await subscriptionAPI.getLatestStaticSiteSubscription();
    if (response.success && response.data) {
      // Only set as expired if it's not cancelled and period has ended
      const isExpired = response.data.currentPeriodEnd && new Date(response.data.currentPeriodEnd) < new Date();
      const isCancelled = response.data.cancelAtPeriodEnd === true;
      set({ expiredStaticSiteSubscription: isExpired && !isCancelled ? response.data : null });
    } else {
      set({ expiredStaticSiteSubscription: null });
    }
  },
}));

const postNonAuthenticate = async () => {
  onAppLoad();
  // get the cart from the local storage
  const cartId = localStorage.getItem('cartId');
  if (cartId) await useCartStore.getState().getCartById(cartId);
};

const postAuthenticate = async () => {
  onAppLoad();
  // 1. handle cart
  // if the user is signed in, attach the cart to the user if the cart exists
  const cartId = localStorage.getItem('cartId');
  if (cartId) useCartStore.getState().mergeLocalCart(cartId);
  else useCartStore.getState().getMyCart(true);
};

const onAppLoad = async () => {
  // get prices
  const response = await priceAPI.getPrices();
  if (response.success) useCartStore.setState({ prices: response.data });
};
